/**
 * Global error handling utilities for production-ready error management
 */

import { captureGlobalError, captureApiError, captureNetworkError } from './sentry';

interface ErrorData {
  message: string;
  stack?: string;
  timestamp: string;
  url: string;
  userAgent: string;
  type: 'javascript' | 'promise' | 'network' | 'api';
  context?: Record<string, any>;
}

class ErrorHandler {
  private static instance: ErrorHandler;
  private errorQueue: ErrorData[] = [];
  private isInitialized = false;

  public static getInstance(): ErrorHandler {
    if (!ErrorHandler.instance) {
      ErrorHandler.instance = new ErrorHandler();
    }
    return ErrorHandler.instance;
  }

  /**
   * Initialize global error handling
   */
  public initialize(): void {
    if (this.isInitialized) {
      return;
    }

    this.setupUnhandledPromiseRejectionHandler();
    this.setupGlobalErrorHandler();
    this.isInitialized = true;

    console.log('Global error handling initialized');
  }

  /**
   * Set up handler for unhandled promise rejections
   */
  private setupUnhandledPromiseRejectionHandler(): void {
    window.addEventListener('unhandledrejection', (event) => {
      console.error('Unhandled promise rejection:', event.reason);
      
      const errorData: ErrorData = {
        message: this.extractErrorMessage(event.reason),
        stack: event.reason?.stack,
        timestamp: new Date().toISOString(),
        url: window.location.href,
        userAgent: navigator.userAgent,
        type: 'promise',
        context: {
          reason: event.reason,
          promise: 'Promise rejection'
        }
      };

      this.logError(errorData);
      
      // Forward to Sentry
      captureGlobalError(event.reason, {
        type: 'promise',
        url: window.location.href,
        userAgent: navigator.userAgent,
        timestamp: errorData.timestamp,
      });
      
      // Prevent the default console error
      if (!import.meta.env.DEV) {
        event.preventDefault();
      }
    });
  }

  /**
   * Set up handler for global JavaScript errors
   */
  private setupGlobalErrorHandler(): void {
    window.addEventListener('error', (event) => {
      console.error('Global JavaScript error:', event.error);
      
      const errorData: ErrorData = {
        message: event.message || this.extractErrorMessage(event.error),
        stack: event.error?.stack,
        timestamp: new Date().toISOString(),
        url: window.location.href,
        userAgent: navigator.userAgent,
        type: 'javascript',
        context: {
          filename: event.filename,
          lineno: event.lineno,
          colno: event.colno,
        }
      };

      this.logError(errorData);
      
      // Forward to Sentry
      captureGlobalError(event.error, {
        type: 'javascript',
        url: window.location.href,
        userAgent: navigator.userAgent,
        timestamp: errorData.timestamp,
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
      });
    });
  }

  /**
   * Log API errors with context
   */
  public logApiError(error: any, context: Record<string, any> = {}): void {
    const errorData: ErrorData = {
      message: this.extractErrorMessage(error),
      stack: error?.stack,
      timestamp: new Date().toISOString(),
      url: window.location.href,
      userAgent: navigator.userAgent,
      type: 'api',
      context: {
        ...context,
        status: error?.response?.status,
        statusText: error?.response?.statusText,
        method: error?.config?.method,
        endpoint: error?.config?.url,
      }
    };

    this.logError(errorData);
    
    // Forward to Sentry
    captureApiError(error, context);
  }

  /**
   * Log network errors
   */
  public logNetworkError(error: any, context: Record<string, any> = {}): void {
    const errorData: ErrorData = {
      message: this.extractErrorMessage(error),
      stack: error?.stack,
      timestamp: new Date().toISOString(),
      url: window.location.href,
      userAgent: navigator.userAgent,
      type: 'network',
      context
    };

    this.logError(errorData);
    
    // Forward to Sentry
    captureNetworkError(error, context);
  }

  /**
   * Extract meaningful error message from various error types
   */
  private extractErrorMessage(error: any): string {
    if (typeof error === 'string') {
      return error;
    }
    
    if (error?.message) {
      return error.message;
    }
    
    if (error?.reason) {
      return this.extractErrorMessage(error.reason);
    }
    
    return 'Unknown error occurred';
  }

  /**
   * Store error data and manage queue
   */
  private logError(errorData: ErrorData): void {
    // Add to queue
    this.errorQueue.push(errorData);
    
    // Keep only the last 50 errors in memory
    if (this.errorQueue.length > 50) {
      this.errorQueue.shift();
    }

    // Store in sessionStorage for debugging
    this.persistErrorToStorage(errorData);

    // In production, you would send this to your monitoring service
    // For now, we'll console.error in development
    if (import.meta.env.DEV) {
      console.group(`🚨 ${errorData.type.toUpperCase()} ERROR`);
      console.error('Message:', errorData.message);
      console.error('Context:', errorData.context);
      if (errorData.stack) {
        console.error('Stack:', errorData.stack);
      }
      console.groupEnd();
    }
  }

  /**
   * Persist error to sessionStorage for debugging
   */
  private persistErrorToStorage(errorData: ErrorData): void {
    try {
      const existingErrors = JSON.parse(sessionStorage.getItem('app_errors') || '[]');
      existingErrors.push(errorData);
      
      // Keep only the last 20 errors to prevent storage bloat
      if (existingErrors.length > 20) {
        existingErrors.splice(0, existingErrors.length - 20);
      }
      
      sessionStorage.setItem('app_errors', JSON.stringify(existingErrors));
    } catch (storageError) {
      console.warn('Failed to persist error to storage:', storageError);
    }
  }

  /**
   * Get all logged errors (useful for debugging)
   */
  public getErrorQueue(): ErrorData[] {
    return [...this.errorQueue];
  }

  /**
   * Clear error queue
   */
  public clearErrorQueue(): void {
    this.errorQueue = [];
    try {
      sessionStorage.removeItem('app_errors');
    } catch (error) {
      console.warn('Failed to clear error storage:', error);
    }
  }
}

// Export singleton instance
export const errorHandler = ErrorHandler.getInstance();

/**
 * Initialize global error handling - call this in main.tsx
 */
export const initializeGlobalErrorHandling = (): void => {
  errorHandler.initialize();
};

/**
 * Utility to wrap async functions with error handling
 */
export const withErrorHandling = <T extends (...args: any[]) => Promise<any>>(
  fn: T,
  context?: Record<string, any>
): T => {
  return ((...args: any[]) => {
    return fn(...args).catch((error) => {
      errorHandler.logApiError(error, { 
        functionName: fn.name,
        ...context 
      });
      throw error; // Re-throw to maintain normal error flow
    });
  }) as T;
};

/**
 * Utility to safely execute async operations
 */
export const safeAsync = async <T>(
  operation: () => Promise<T>,
  fallback?: T,
  context?: Record<string, any>
): Promise<T | undefined> => {
  try {
    return await operation();
  } catch (error) {
    errorHandler.logApiError(error, context);
    return fallback;
  }
}; 